// Speech-to-text contract — the single result shape every transcription
// producer emits and every consumer reads.
//
// Today there is exactly one producer: the extension's offscreen Whisper
// pipeline (source: 'local'), driven by the per-message Transcribe buttons
// and — via `waGetMessageMediaTool({transcribeAudio: true})` — available to
// any engine-dispatch caller. The shape is designed so a future server-side
// provider (source: 'cloud') can slot in behind the same contract without
// touching any consumer: same fields, different `source`/`model`.
//
// Design rules:
//   * The result is ALWAYS structured — never a bare string. Consumers that
//     act on transcripts (an agent) need `flags` to decide between acting
//     and asking the sender to repeat; UIs need `model`/`backend` for
//     diagnostics.
//   * "Failed" transcriptions are results with flags, not throws. Silence
//     is a legitimate outcome; the caller decides how to render it.
//   * Additive evolution only — this crosses the extension↔backend wire
//     (bt-common is mirrored into both repos), so new fields must be
//     optional and existing fields must never change meaning.

import { z } from 'zod';

/** Quality/validity signals attached to a transcription result.
 *
 *  - `silence`          — pre-guard: no speech energy detected; `text` is
 *                         empty BY DESIGN and no model was run.
 *  - `low-confidence`   — the pipeline produced empty/dubious output on
 *                         audio that does contain speech energy. Consumers
 *                         acting on the text should confirm with the sender.
 *  - `repetition-loop`  — post-guard: the text contains a pathological
 *                         repeated n-gram run (Whisper's classic
 *                         hallucination signature). Text is returned but
 *                         should be treated as suspect.
 *  - `truncated`        — audio exceeded the max-duration policy; the
 *                         transcript covers only the leading window. */
export const TranscriptionFlagSchema = z.enum([
  'silence',
  'low-confidence',
  'repetition-loop',
  'truncated',
]);
export type TranscriptionFlag = z.infer<typeof TranscriptionFlagSchema>;

export const TranscriptionResultSchema = z.object({
  /** The transcript. Empty string when flags include 'silence'. */
  text: z.string(),
  /** ISO-639-1 code — user pref, sticky chat language, or detected.
   *  Null when detection failed and no hint was available. */
  language: z.string().nullable(),
  /** How the language was determined — lets consumers distinguish "the
   *  user locked Hebrew" from "the model guessed Hebrew on 6s of audio". */
  languageSource: z.enum(['user', 'sticky', 'detected', 'unknown']),
  /** Decoded audio duration. */
  durationMs: z.number(),
  /** Wall-clock time of the transcription pass itself (excludes model
   *  download; useful for latency budgeting + metrics). */
  processingMs: z.number(),
  /** HuggingFace model id (e.g. 'ivrit-ai/whisper-large-v3-turbo-onnx',
   *  'Xenova/whisper-small') — or a provider-prefixed id for future cloud
   *  sources (e.g. 'groq:whisper-large-v3-turbo'). */
  model: z.string(),
  /** Inference backend the run actually used. 'n/a' for guard-only results
   *  (silence short-circuits before any model is touched) and for future
   *  cloud providers where the backend is opaque. */
  backend: z.enum(['webgpu', 'wasm', 'n/a']),
  /** Where the transcription ran. 'local' = extension offscreen Whisper.
   *  'cloud' reserved for a future server-side provider. */
  source: z.enum(['local', 'cloud']),
  flags: z.array(TranscriptionFlagSchema),
});
export type TranscriptionResult = z.infer<typeof TranscriptionResultSchema>;

/** Convenience guard: true when the transcript is trustworthy enough to
 *  act on without confirming with the sender (no flags at all, or only
 *  'truncated' — a truncated transcript is still accurate for the window
 *  it covers). */
export function isActionableTranscription(r: TranscriptionResult): boolean {
  return r.flags.every((f) => f === 'truncated');
}
